import React, { useState, useMemo } from 'react';
import { Search, Eye, Calendar, ChevronDown, FileText, CheckSquare, Square, RotateCcw, Package, ArrowLeftRight } from 'lucide-react';
import { Product, ToastMessage, Transaction } from '../../types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReceiptModal from '../POS/ReceiptModal';
import * as XLSX from 'xlsx';

interface TransactionListProps {
    transactions: Transaction[];
    onViewReceipt: (transaction: Transaction) => void;
    onReturn?: (transaction: Transaction) => void;
    onExchange?: (transaction: Transaction) => void;
    refreshTransactions: () => Promise<void> | void;
    refreshProducts: () => Promise<void> | void;
    inventory?: Product[];
    currentUser: string;
    addToast: (type: ToastMessage['type'], title: string, message: string) => void;
}

const TransactionList: React.FC<TransactionListProps> = ({
    transactions,
    onViewReceipt,
    onReturn,
    onExchange,
    refreshTransactions,
    refreshProducts,
    inventory = [],
    currentUser,
    addToast,
}) => {
    const [expandedTransactions, setExpandedTransactions] = useState<Set<string>>(new Set());
    console.log(transactions);
    const toggleTransaction = (id: string) => {
        const next = new Set(expandedTransactions);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpandedTransactions(next);
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'Detailed' | 'Summary'>('Detailed');
    const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
    const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set<string>());
    const [receiptTransaction, setReceiptTransaction] = useState<Transaction | null>(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toLocaleDateString('en-CA');
    });
    const [endDate, setEndDate] = useState(() => new Date().toLocaleDateString('en-CA'));
    console.log(currentUser);
    const filtered = useMemo(() => {
        return transactions.filter(t => {
            const isSale = String(t.type).toLowerCase() === 'sale';
            const matchesSearch =
                t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (t.customer?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
            const inRange = t.businessDate >= startDate && t.businessDate <= endDate;
            return isSale && matchesSearch && inRange;
        });
    }, [transactions, searchTerm, startDate, endDate]);

    const groupedByDay = useMemo(() => {
        const grouped: Record<string, { total: number; cash: number; card: number; count: number; items: Transaction[] }> = {};
        filtered.forEach(t => {
            const day = t.businessDate;
            if (!grouped[day]) grouped[day] = { total: 0, cash: 0, card: 0, count: 0, items: [] };
            grouped[day].total += t.total;
            if (t.paymentMethod === 'Cash') grouped[day].cash += t.amountPaid;
            if (t.paymentMethod === 'Card') grouped[day].card += t.amountPaid;
            grouped[day].count += 1;
            grouped[day].items.push(t);
        });
        return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
    }, [filtered]);

    const toggleSelectDay = (day: string) => {
        const next = new Set(selectedDays);
        if (next.has(day)) next.delete(day);
        else next.add(day);
        setSelectedDays(next);
    };

    const toggleAllDays = () => {
        if (selectedDays.size === groupedByDay.length) setSelectedDays(new Set());
        else setSelectedDays(new Set(groupedByDay.map(([day]) => day)));
    };

    // ── Status badge ──────────────────────────────────────────────────────
    const getStatusBadge = (t: Transaction) => {
        if (t.isReturned) {
            return <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-red-50 text-red-500">Returned</span>;
        } else if (t.isExchanged) {
            return <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-red-50 text-red-500">Exchanged</span>;
        }
        return <span className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600">Settled</span>;
    };

    // ── Action buttons ────────────────────────────────────────────────────
    const getActionButtons = (t: Transaction) => (
        <div className="flex justify-center gap-1">
            <button
                onClick={() => setReceiptTransaction(t)}
                className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                title="View Receipt"
            >
                <Eye size={16} />
            </button>
            {onExchange && !t.isExchanged && !t.isReturned && (
                <button
                    onClick={() => onExchange(t)}
                    className={!t.isExchanged ? "p-2 text-gray-400 hover:text-amber-500 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors" : "p-2 text-gray-400 hover:text-grey-500 rounded-lg hover:bg-grey-50 dark:hover:bg-grey-900/20 transition-colors"}
                    title="Exchange"
                >
                    <ArrowLeftRight size={16} />
                </button>
            )}
            {onReturn && !t.isReturned && (
                <button
                    onClick={() => onReturn(t)}
                    className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Return"
                >
                    <RotateCcw size={16} />
                </button>
            )}
        </div>
    );

    // ── PDF: Sales Report ─────────────────────────────────────────────────
    const generateBifurcatedPDF = () => {
        if (selectedDays.size === 0) { alert("Please select dates in Summary mode."); return; }
        const doc = new jsPDF('l', 'mm', 'a4');
        const now = new Date();
        const timeStamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 12);
        doc.setFontSize(18); doc.setTextColor(0, 0, 0);
        doc.text('NIAMIA SALES & REPLACEMENTS AUDIT', 14, 15);
        doc.setFontSize(9);
        doc.text(`Generated: ${now.toLocaleString()}`, 14, 22);
        let finalY = 25; let grandTotal = 0;
        const sortedSelectedDays = (Array.from(selectedDays) as string[]).sort((a, b) => b.localeCompare(a));
        sortedSelectedDays.forEach((day) => {
            const dayData = groupedByDay.find(([d]) => d === day);
            if (!dayData) return;
            const dailyNetTotal = dayData[1].items.filter(txn => !txn.isReturned).reduce((acc, curr) => acc + (curr.total || 0), 0);
            grandTotal += dailyNetTotal;
            if (finalY > 170) { doc.addPage(); finalY = 15; }
            doc.setFontSize(12); doc.setTextColor(0, 0, 0);
            doc.text(`DATE: ${day}`, 14, finalY + 5);
            const rows: any[] = [];
            dayData[1].items.forEach(txn => {
                (txn.items || []).forEach(item => {
                    const itemPrice = item.price || 0; const itemQty = item.quantity || 0;
                    const itemSubtotal = itemPrice * itemQty;
                    const customerLabel = txn.isReturned ? `${txn.customer?.name || 'Walk-in'} (REP)` : (txn.customer?.name || 'Walk-in');
                    rows.push([customerLabel, item.name || 'Unknown Article', item.selectedSize?.size || '-', itemQty, itemPrice.toLocaleString(), txn.paymentMethod || '-', txn.isReturned ? `(${itemSubtotal.toLocaleString()})` : itemSubtotal.toLocaleString(), txn.isReturned]);
                });
            });
            autoTable(doc, {
                startY: finalY + 10,
                head: [['Customer', 'Article', 'Size', 'Qty', 'Price', 'Method', 'Subtotal']],
                body: rows.map(r => r.slice(0, 7)),
                foot: [[{ content: `Daily Total`, colSpan: 6, styles: { halign: 'right' } }, { content: dailyNetTotal.toLocaleString() }]],
                theme: 'plain',
                headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [200, 200, 200] },
                footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [200, 200, 200] },
                styles: { fontSize: 8, textColor: [0, 0, 0] },
                didParseCell: (data) => { if (data.section !== 'body') return; const row = rows[data.row.index]; if (row?.[7]) { data.cell.styles.fontStyle = 'italic'; data.cell.styles.textColor = [120, 120, 120]; } },
                margin: { left: 14 },
            });
            finalY = (doc as any).lastAutoTable.finalY + 10;
        });
        if (finalY > 160) { doc.addPage(); finalY = 20; }
        doc.setDrawColor(0, 0, 0); doc.line(14, finalY, 283, finalY);
        autoTable(doc, { startY: finalY + 5, head: [['Report Summary', 'Value']], body: [['Total Selected Dates', selectedDays.size.toString()], ['Grand Net Revenue', grandTotal.toLocaleString()]], theme: 'plain', headStyles: { fontStyle: 'bold' }, styles: { cellPadding: 2 } });
        doc.save(`Store_Sales_${timeStamp}.pdf`);
    };

    // ── PDF: Inventory Snapshot ───────────────────────────────────────────
    const generateInventoryPDF = () => {
        if (!inventory || inventory.length === 0) { alert("No inventory data available."); return; }
        const doc = new jsPDF('l', 'mm', 'a4');
        const now = new Date();
        const timeStamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 12);
        doc.setFontSize(18); doc.setTextColor(0, 0, 0);
        doc.text('NIAMIA INVENTORY SNAPSHOT', 14, 15);
        doc.setFontSize(9); doc.text(`Generated: ${now.toLocaleString()}`, 14, 22);
        const rows: (string | number)[][] = [];
        inventory.forEach(item => {
            if (item.stocks && item.stocks.length > 0) {
                item.stocks.forEach(sv => { if (Number(sv.stock) === 0) return; rows.push([item.id || '-', item.name || 'Unknown', item.collection || '-', sv.size || '-', sv.stock, item.price != null ? item.price.toLocaleString() : '-']); });
            } else {
                if (Number(item.stock ?? 0) === 0) return;
                rows.push([item.id || '-', item.name || 'Unknown', item.category || '-', '-', item.stock ?? 0, item.price != null ? item.price.toLocaleString() : '-']);
            }
        });
        rows.sort((a, b) => { const c = String(a[2]).localeCompare(String(b[2])); if (c !== 0) return c; const n = String(a[1]).localeCompare(String(b[1])); if (n !== 0) return n; return String(a[3]).localeCompare(String(b[3])); });
        if (rows.length === 0) { alert("All stock quantities are zero — nothing to report."); return; }
        const totalUnits = rows.reduce((acc, r) => acc + Number(r[4] ?? 0), 0);
        autoTable(doc, { startY: 28, head: [['SKU / ID', 'Article Name', 'Category', 'Size', 'Qty in Stock', 'Unit Price (Rs.)']], body: rows, foot: [[{ content: `Total SKUs: ${rows.length}`, colSpan: 4, styles: { halign: 'right' } }, { content: totalUnits.toString(), styles: { fontStyle: 'bold' } }, '']], theme: 'plain', headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [200, 200, 200] }, footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [200, 200, 200] }, styles: { fontSize: 8, textColor: [0, 0, 0] }, columnStyles: { 4: { halign: 'center' }, 5: { halign: 'right' } }, margin: { left: 14 } });
        doc.save(`Store_Inventory_${timeStamp}.pdf`);
    };

    // ── XLSX ──────────────────────────────────────────────────────────────
    const generateBifurcatedXLSX = () => {
        if (selectedDays.size === 0) { alert("Please select dates in Summary mode."); return; }
        const wb = XLSX.utils.book_new();
        const now = new Date();
        const timeStamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 12);
        const rows: (string | number)[][] = [];
        let grandTotal = 0;
        rows.push(['NIAMIA SALES & REPLACEMENTS AUDIT']);
        rows.push([`Generated: ${now.toLocaleString()}`]);
        rows.push([]);
        Array.from(selectedDays).sort((a, b) => b.localeCompare(a)).forEach(day => {
            const dayData = groupedByDay.find(([d]) => d === day);
            if (!dayData) return;
            const dailyNetTotal = dayData[1].items.filter(txn => !txn.isReturned).reduce((acc, curr) => acc + (curr.total || 0), 0);
            grandTotal += dailyNetTotal;
            rows.push([`DATE: ${day}`]);
            rows.push(['Customer', 'Article', 'Size', 'Qty', 'Price', 'Method', 'Subtotal', 'Status']);
            dayData[1].items.forEach(txn => {
                (txn.items || []).forEach(item => {
                    const p = item.price || 0; const q = item.quantity || 0;
                    rows.push([txn.customer?.name || 'Walk-in', item.name || 'Unknown Article', item.selectedSize?.size || '-', q, p, txn.paymentMethod || '-', txn.isReturned ? -(p * q) : p * q, txn.isReturned ? 'Returned' : 'Settled']);
                });
            });
            rows.push(['', '', '', '', '', 'Daily Total', dailyNetTotal, '']);
            rows.push([]);
        });
        rows.push(['Report Summary']); rows.push(['Total Selected Dates', selectedDays.size]); rows.push(['Grand Net Revenue', grandTotal]);
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = [{ wch: 24 }, { wch: 28 }, { wch: 10 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Sales Audit');
        XLSX.writeFile(wb, `Store_Sales_${timeStamp}.xlsx`);
    };

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50 dark:bg-gray-950 transition-colors duration-200">
            <div className="max-w-7xl mx-auto space-y-8">
                <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Sales Ledger</h1>
                        <div className="flex items-center gap-1 mt-4 p-1 bg-gray-200/50 dark:bg-gray-800 rounded-xl w-fit border border-gray-200 dark:border-gray-700">
                            <button onClick={() => setViewMode('Detailed')} className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'Detailed' ? 'bg-white dark:bg-gray-700 text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>Detailed</button>
                            <button onClick={() => setViewMode('Summary')} className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'Summary' ? 'bg-white dark:bg-gray-700 text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>Daily Summary</button>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <button onClick={generateInventoryPDF} className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-500/20">
                            <Package size={16} /> Download Inventory
                        </button>
                        {viewMode === 'Summary' ? (
                            <>
                                <button onClick={generateBifurcatedPDF} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20">
                                    <FileText size={16} /> PDF Report ({selectedDays.size})
                                </button>
                                <button onClick={generateBifurcatedXLSX} className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-teal-700 transition-all shadow-lg shadow-teal-500/20">
                                    <FileText size={16} /> Export XLSX
                                </button>
                            </>
                        ) : (
                            <div className="flex gap-3 bg-white dark:bg-gray-900 p-2 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm">
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent text-[10px] font-black uppercase outline-none dark:text-white px-2" />
                                <span className="text-gray-300">|</span>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent text-[10px] font-black uppercase outline-none dark:text-white px-2" />
                            </div>
                        )}
                    </div>
                </header>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" placeholder="Find Sales, Customers, Invoice ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-6 py-4 bg-white dark:bg-gray-900 rounded-[1.5rem] border border-gray-100 dark:border-gray-800 font-black text-xs uppercase tracking-widest outline-none shadow-sm transition-all focus:ring-4 focus:ring-indigo-500/5" />
                </div>

                {viewMode === 'Detailed' ? (
                    <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden animate-in fade-in">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50 dark:bg-gray-800/50 text-[10px] font-black uppercase text-gray-400 tracking-[0.2em] border-b border-gray-100 dark:border-gray-800">
                                <tr>
                                    <th className="p-6 w-8"></th> {/* chevron */}

                                    <th className="p-6">Timestamp & ID</th>
                                    <th className="p-6">Client Identity</th>
                                    <th className="p-6">Status</th>
                                    <th className="p-6 text-right">Net Value</th>
                                    <th className="p-6 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                {filtered.map(t => {
                                    const isExpanded = expandedTransactions.has(t.id);
                                    return (
                                        <React.Fragment key={t.id}>
                                            {/* Main row */}
                                            <tr
                                                className={`transition-colors cursor-pointer ${t.isReturned ? 'bg-red-50/30 dark:bg-red-950/10' : 'hover:bg-gray-50/50 dark:hover:bg-gray-800/30'}`}
                                                onClick={() => toggleTransaction(t.id)}
                                            >
                                                <td className="p-6 pl-4 w-8">
                                                    <ChevronDown
                                                        size={16}
                                                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                    />
                                                </td>
                                                <td className="p-6">
                                                    <div className="text-[11px] font-black dark:text-white">{new Date(t.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                                                    <div className="text-[9px] font-mono text-gray-400">{new Date(t.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
                                                </td>
                                                <td className="p-6 text-[11px] font-bold dark:text-gray-300 uppercase">{t.customer?.name || 'Walk-in Guest'}</td>
                                                <td className="p-6">{getStatusBadge(t)}</td>
                                                <td className="p-6 text-right font-black text-sm dark:text-white">Rs. {t.total.toLocaleString()}</td>
                                                <td className="p-6 text-center" onClick={e => e.stopPropagation()}>{getActionButtons(t)}</td>
                                            </tr>

                                            {/* Articles dropdown row */}
                                            {isExpanded && (
                                                <tr className={t.isReturned ? 'bg-red-50/20 dark:bg-red-950/5' : 'bg-gray-50/50 dark:bg-gray-800/20'}>
                                                    <td colSpan={6} className="px-8 pb-4 pt-0">
                                                        <table className="w-full text-left">
                                                            <thead>
                                                                <tr className="text-[8px] font-black uppercase text-gray-400 border-b border-gray-100 dark:border-gray-800">
                                                                    <th className="py-2 pr-4">Article</th>
                                                                    <th className="py-2 pr-4">Size</th>
                                                                    <th className="py-2 pr-4 text-center">Qty</th>
                                                                    <th className="py-2 text-right">Unit Price</th>
                                                                    <th className="py-2 text-right">Subtotal</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(t.items || []).map((item, idx) => (
                                                                    <tr key={idx} className="border-b border-gray-50 dark:border-gray-800 last:border-0">
                                                                        <td className="py-2 pr-4 text-[10px] font-black dark:text-white uppercase">{item.name || 'Unknown'}</td>
                                                                        <td className="py-2 pr-4 text-[10px] font-bold text-gray-400">{item.selectedSize?.size || '-'}</td>
                                                                        <td className="py-2 pr-4 text-[10px] font-black text-center dark:text-white">{item.quantity}</td>
                                                                        <td className="py-2 text-right text-[10px] font-bold text-gray-500">Rs. {(item.price || 0).toLocaleString()}</td>
                                                                        <td className={`py-2 text-right text-[10px] font-black ${t.isReturned ? 'text-red-400 line-through' : 'dark:text-white'}`}>
                                                                            Rs. {((item.price || 0) * (item.quantity || 0)).toLocaleString()}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="space-y-4 animate-in fade-in">
                        {groupedByDay.length > 0 && (
                            <div className="flex items-center justify-between px-2">
                                <button onClick={toggleAllDays} className="text-[9px] font-black text-indigo-500 hover:text-indigo-700 uppercase tracking-widest transition-colors">
                                    {selectedDays.size === groupedByDay.length ? 'Deselect All' : 'Select All'}
                                </button>
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                                    {selectedDays.size} of {groupedByDay.length} days selected
                                </span>
                            </div>
                        )}
                        {groupedByDay.map(([day, data]) => {
                            const isExpanded = expandedDays.has(day);
                            const isSelected = selectedDays.has(day);
                            return (
                                <div key={day} className={`bg-white dark:bg-gray-900 rounded-[2rem] border transition-all ${isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/10' : 'border-gray-100 dark:border-gray-800'}`}>
                                    <div className="p-6 flex items-center gap-4">
                                        <button onClick={() => toggleSelectDay(day)} className={`p-2 rounded-lg transition-colors shrink-0 ${isSelected ? 'text-indigo-600 bg-indigo-50' : 'text-gray-300'}`}>
                                            {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                                        </button>
                                        <div onClick={() => { const next = new Set(expandedDays); isExpanded ? next.delete(day) : next.add(day); setExpandedDays(next); }} className="flex-1 flex justify-between items-center cursor-pointer">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-3 rounded-2xl shrink-0 ${isExpanded ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-indigo-600'}`}><Calendar size={20} /></div>
                                                <div>
                                                    <h3 className="text-lg font-black dark:text-white uppercase leading-none">{day}</h3>
                                                    <p className="text-[9px] font-black text-gray-400 mt-1 uppercase tracking-widest">{data.count} Sales</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6">
                                                <div className="text-right">
                                                    <p className="text-[8px] font-black text-gray-400 uppercase mb-0.5">Daily Total</p>
                                                    <p className="text-xl font-black text-indigo-600">Rs. {data.total.toLocaleString()}</p>
                                                </div>
                                                <ChevronDown size={20} className={`transition-transform text-gray-400 ${isExpanded ? 'rotate-180' : ''}`} />
                                            </div>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="border-t border-gray-50 dark:border-gray-800 overflow-hidden">
                                            <table className="w-full text-left bg-gray-50/30 dark:bg-gray-950/30">
                                                <thead className="text-[8px] font-black uppercase text-gray-400 border-b border-gray-100 dark:border-gray-800">
                                                    <tr>
                                                        <th className="p-4 pl-8">Invoice</th>
                                                        <th className="p-4">Customer</th>
                                                        <th className="p-4">Status</th>
                                                        <th className="p-4">Method</th>
                                                        <th className="p-4 text-right">Total</th>
                                                        <th className="p-4 text-center pr-8">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.items.map(txn => (
                                                        <tr key={txn.id} className="hover:bg-white dark:hover:bg-gray-900 transition-colors">
                                                            <td className="p-4 pl-8 font-mono text-[10px] text-indigo-600 cursor-pointer hover:underline" onClick={() => setReceiptTransaction(txn)}>{txn.id}</td>
                                                            <td className="p-4 text-[10px] font-black uppercase dark:text-gray-300">{txn.customer?.name || 'Walk-in'}</td>
                                                            <td className="p-4">
                                                                {txn.isReturned
                                                                    ? <span className="text-[9px] font-black text-red-400 uppercase">Returned</span>
                                                                    : <span className="text-[9px] font-black text-emerald-500 uppercase">Settled</span>
                                                                }
                                                            </td>
                                                            <td className="p-4 text-[10px] text-gray-400 uppercase font-black">{txn.paymentMethod}</td>
                                                            <td className="p-4 text-right font-black text-[10px] dark:text-white">Rs. {txn.total.toLocaleString()}</td>
                                                            <td className="p-4 pr-8 text-center">
                                                                <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                                                                    <button onClick={() => setReceiptTransaction(txn)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors" title="View Receipt"><Eye size={14} /></button>
                                                                    {onExchange && !txn.isExchanged && !txn.isReturned && (
                                                                        <button onClick={() => onExchange(txn)} className="p-1.5 text-gray-400 hover:text-amber-500 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors" title="Exchange"><ArrowLeftRight size={14} /></button>
                                                                    )}
                                                                    {onReturn && !txn.isReturned && (
                                                                        <button onClick={() => onReturn(txn)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Return"><RotateCcw size={14} /></button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ReceiptModal
                transaction={receiptTransaction}
                onClose={() => setReceiptTransaction(null)}
                addToast={addToast}
            />

        </div>
    );
};

export default TransactionList;